import { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { useDealershipValidationSchema } from "./validationSchemas";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { getCurrencySymbol } from "../../../utils/currencySymbols";
import API_ENDPOINTS from "../../../api/apiEndpoints";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../../shared/Services/Loader/loaderService";
import FormLabel from "../../../shared/helpers/FormLabel";
import { US_STATES } from "../../../shared/helpers/constants";
import { formatZipCode } from "../../../shared/helpers/formUtils";
import { useLocalization } from "../../../context/LocalizationContext";
import apiManager from "../../../api/apiManager";
import { encryptPayload } from "../../../utils/EncryptionDecryption";

const getBaseInitialValues = (tenantId) => ({
  name: "",
  address1: "",
  address2: "",
  address3: "",
  cityOrLocality: "",
  stateOrProvinceCode: "",
  postalCodeOrZip: "",
  timeZoneCode: "",
  languageCode: "",
  tenantId: tenantId || "",
  selectedServices: [],
  logoPath: "",
  hourlyLabourRate: "",
  tax: "",
});

const CreateDealerShip = () => {
  const { t: localization } = useLocalization();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [tenantId, setTenantId] = useState(localStorage.getItem("tenantId"));
  const [uploadError, setUploadError] = useState("");
  const [services, setServices] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [initialValues, setInitialValues] = useState(() =>
    isEditMode ? null : getBaseInitialValues(localStorage.getItem("tenantId")),
  );
  const validationSchema = useDealershipValidationSchema();

  useEffect(() => {
    fetchServices();
    const storedTenant = localStorage.getItem("tenantId");
    setTenantId(storedTenant);
    if (isEditMode) {
      fetchServiceDetails(id);
    } else {
      setInitialValues(getBaseInitialValues(storedTenant));
    }
  }, [id, isEditMode]);

  useEffect(() => {
    if (initialValues?.stateOrProvinceCode) {
      fetchCitiesByState(initialValues.stateOrProvinceCode);
    }
  }, [initialValues?.stateOrProvinceCode]);

  useEffect(() => {
    return () => {
      if (
        logoPreview &&
        typeof logoPreview === "string" &&
        logoPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const fetchCitiesByState = async (stateCode) => {
    if (!stateCode) {
      setAvailableCities([]);
      return;
    }
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.DROPDOWN_CITIES_BY_STATE,
        { stateCode },
      );
      if (response.status === 200) {
        setAvailableCities(response.data || []);
      } else {
        showErrorMessage(localization("failed_fetch_cities"));
        setAvailableCities([]);
      }
    } catch (error) {
      showErrorMessage(error.message || localization("error_fetching_cities"));
      setAvailableCities([]);
    } finally {
      hideLoader();
    }
  };

  const fetchServiceDetails = async (dealerId) => {
    try {
      showLoader();
      const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
        id: dealerId,
      });
      if (response.status === 200) {
        const data = response.data;
        const mappedSelectedServices = (data.selectedServices || []).map(
          (s) => s.dealerServiceId,
        );
        setLogoPreview(data.logoPath || null);
        setInitialValues({
          ...data,
          selectedServices: mappedSelectedServices,
          hourlyLabourRate: data.hourlyLabourRate ?? "",
          tax: data.tax ?? "",
        });
      } else {
        showErrorMessage(localization("failed_fetch_service_details"));
      }
    } catch (e) {
      showErrorMessage(localization("error_fetching_shop"), e.message);
    } finally {
      hideLoader();
    }
  };

  const handleSubmit = async (values) => {
    showLoader();
    try {
      const formData = new FormData();
      const payload = {};
      let fileKey = null;
      let fileValue = null;

      const formValues = {
        ...values,
        tenantId: values.tenantId || tenantId,
        hourlyLabourRate:
          values.hourlyLabourRate !== ""
            ? Number(values.hourlyLabourRate)
            : null,
        tax: values.tax !== "" ? Number(values.tax) : null,
      };

      if (isEditMode) {
        formValues.id = id;
      }

      Object.entries(formValues).forEach(([key, value]) => {
        if (value instanceof File) {
          fileKey = key;
          fileValue = value;
          return;
        }
        if (key === "logoPath") {
          return;
        }
        payload[key] = value;
      });

      const json = JSON.stringify(payload);
      const encryptedPayload = await encryptPayload(json);
      formData.append("encryptedPayload", encryptedPayload);

      if (fileKey && fileValue) {
        formData.append(fileKey, fileValue);
      }

      const response = await apiManager.post(
        isEditMode ? API_ENDPOINTS.DEALER_UPDATE : API_ENDPOINTS.DEALER_CREATE,
        formData
      );

      if (response.status === 200) {
        showSuccessMessage(
          localization(isEditMode ? "shop_updated" : "shop_created"),
        );
        try {
          window.dispatchEvent(new Event("dealershipsChanged"));
          window.localStorage.setItem(
            "dealershipsVersion",
            Date.now().toString(),
          );
        } catch (_) {}
        navigate("/services/dealership/list");
      } else {
        showErrorMessage(
          localization(
            isEditMode ? "failed_update_shop" : "failed_create_shop",
          ),
        );
      }
    } catch (error) {
      showErrorMessage(error);
    } finally {
      hideLoader();
    }
  };

  const fetchServices = async () => {
    try {
      const response = await apiManager.get(
        API_ENDPOINTS.DEALER_GET_SERVICES,
        {},
      );
      if (response.status == 200) {
        const data = response.data;
        setServices(data);
      } else {
        showErrorMessage(localization("failed_fetch_services"));
      }
    } catch (e) {
      showErrorMessage(localization("error_fetching_services"));
    }
  };

  const timeZoneOptions = [
    { value: "UTC-5", label: `${localization("tz_eastern")} (UTC-5)` },
    { value: "UTC-6", label: `${localization("tz_central")} (UTC-6)` },
    { value: "UTC-7", label: `${localization("tz_mountain")} (UTC-7)` },
    { value: "UTC-8", label: `${localization("tz_pacific")} (UTC-8)` },
    { value: "UTC+1", label: `${localization("tz_cet")} (UTC+1)` },
  ];

  const languageOptions = [
    { value: "en", label: localization("language_english") },
    { value: "es", label: localization("language_spanish") },
  ];

  if (isEditMode && !initialValues) {
    return <div className="p-4">{localization("loading_shop")}</div>;
  }

  return (
    <div>
      <div className="container-fluid content-wrapper select-vehicle-content">
        <div className="content form-card">
          <div className="page-title">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-md-6">
                  <h1>{localization(isEditMode ? "edit_shop" : "add_shop")}</h1>
                </div>
                <div className="col-md-6"></div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <Formik
              initialValues={initialValues || getBaseInitialValues(tenantId)}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                errors,
                touched,
                isSubmitting,
                status,
                setFieldValue,
                values,
              }) => (
                <Form>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="name"
                        label={localization("name")}
                        required
                      />
                      <Field
                        name="name"
                        className={`form-control ${
                          touched.name && errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={localization("enter_shop_name")}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="address1"
                        label={localization("address_1")}
                        required
                      />
                      <Field
                        name="address1"
                        className={`form-control ${
                          touched.address1 && errors.address1
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={localization("placeholder_address1")}
                      />
                      {touched.address1 && errors.address1 && (
                        <div className="invalid-feedback">
                          {errors.address1}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="address2"
                        label={localization("address_2")}
                      />
                      <Field
                        name="address2"
                        className={`form-control ${
                          touched.address2 && errors.address2
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={localization("placeholder_address2")}
                      />
                      {touched.address2 && errors.address2 && (
                        <div className="invalid-feedback">
                          {errors.address2}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="address3"
                        label={localization("address_3")}
                      />
                      <Field
                        name="address3"
                        className={`form-control ${
                          touched.address3 && errors.address3
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={localization("placeholder_address3")}
                      />
                      {touched.address3 && errors.address3 && (
                        <div className="invalid-feedback">
                          {errors.address3}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="stateOrProvinceCode"
                        label={localization("state")}
                        required
                      />
                      <div className="dropdown-container">
                        <Select
                          name="stateOrProvinceCode"
                          options={US_STATES.map((state) => ({
                            value: state.code,
                            label: `${state.name} (${state.code})`,
                          }))}
                          classNamePrefix="select"
                          className={
                            touched.stateOrProvinceCode &&
                            errors.stateOrProvinceCode
                              ? "is-invalid"
                              : ""
                          }
                          value={
                            US_STATES.find(
                              (opt) => opt.code === values.stateOrProvinceCode,
                            )
                              ? {
                                  value: values.stateOrProvinceCode,
                                  label: `${
                                    US_STATES.find(
                                      (opt) =>
                                        opt.code === values.stateOrProvinceCode,
                                    ).name
                                  } (${values.stateOrProvinceCode})`,
                                }
                              : null
                          }
                          onChange={(opt) => {
                            setFieldValue(
                              "stateOrProvinceCode",
                              opt ? opt.value : "",
                            );
                            fetchCitiesByState(opt ? opt.value : "");
                            setFieldValue("cityOrLocality", "");
                          }}
                          placeholder={localization("select_state")}
                          isClearable
                        />
                      </div>
                      {touched.stateOrProvinceCode &&
                        errors.stateOrProvinceCode && (
                          <div className="invalid-feedback d-block">
                            {errors.stateOrProvinceCode}
                          </div>
                        )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="cityOrLocality"
                        label={localization("city")}
                        required
                      />
                      <div className="dropdown-container">
                        <Select
                          name="cityOrLocality"
                          options={availableCities.map((city) => ({
                            value: city,
                            label: city,
                          }))}
                          classNamePrefix="select"
                          className={
                            touched.cityOrLocality && errors.cityOrLocality
                              ? "is-invalid"
                              : ""
                          }
                          value={
                            availableCities.find(
                              (city) => city === values.cityOrLocality,
                            )
                              ? {
                                  value: values.cityOrLocality,
                                  label: values.cityOrLocality,
                                }
                              : null
                          }
                          onChange={(opt) =>
                            setFieldValue(
                              "cityOrLocality",
                              opt ? opt.value : "",
                            )
                          }
                          placeholder={localization("select_city")}
                          isClearable
                          isDisabled={availableCities.length === 0}
                        />
                      </div>
                      {touched.cityOrLocality && errors.cityOrLocality && (
                        <div className="invalid-feedback d-block">
                          {errors.cityOrLocality}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="postalCodeOrZip"
                        label={localization("zip")}
                        required
                      />
                      <Field
                        name="postalCodeOrZip"
                        className={`form-control ${
                          touched.postalCodeOrZip && errors.postalCodeOrZip
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(e) => {
                          const formattedValue = formatZipCode(e.target.value);
                          setFieldValue("postalCodeOrZip", formattedValue);
                        }}
                        placeholder={localization("enter_zip")}
                      />
                      {touched.postalCodeOrZip && errors.postalCodeOrZip && (
                        <div className="invalid-feedback">
                          {errors.postalCodeOrZip}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="timeZoneCode"
                        label={localization("time_zone")}
                        required
                      />
                      <div className="dropdown-container">
                        <Select
                          name="timeZoneCode"
                          options={timeZoneOptions}
                          classNamePrefix="select"
                          value={
                            timeZoneOptions.find(
                              (opt) => opt.value === values.timeZoneCode,
                            ) || null
                          }
                          onChange={(opt) =>
                            setFieldValue("timeZoneCode", opt ? opt.value : "")
                          }
                          placeholder={localization("select_time_zone")}
                        />
                        {touched.timeZoneCode && errors.timeZoneCode && (
                          <div className="text-danger mt-1">
                            {errors.timeZoneCode}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="languageCode"
                        label={localization("language")}
                        required
                      />
                      <div className="dropdown-container">
                        <Select
                          name="languageCode"
                          options={languageOptions}
                          classNamePrefix="select"
                          value={
                            languageOptions.find(
                              (opt) => opt.value === values.languageCode,
                            ) || null
                          }
                          onChange={(opt) =>
                            setFieldValue("languageCode", opt ? opt.value : "")
                          }
                          placeholder={localization("select_language")}
                        />
                        {touched.languageCode && errors.languageCode && (
                          <div className="text-danger mt-1">
                            {errors.languageCode}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="row g-2">
                        <div className="col-12 col-md-7">
                          <FormLabel
                            htmlFor="hourlyLabourRate"
                            label={localization("labour_rate")}
                            required
                          />
                          <Field name="hourlyLabourRate">
                            {({ field, form }) => (
                              <input
                                {...field}
                                type="text"
                                inputMode="decimal"
                                pattern="^\d*\.?\d{0,2}$"
                                className={`form-control ${
                                  form.touched.hourlyLabourRate &&
                                  form.errors.hourlyLabourRate
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder={localization(
                                  "hourly_rate_placeholder",
                                )}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key))
                                    e.preventDefault();
                                }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    value === "" ||
                                    /^\d*\.?\d{0,2}$/.test(value)
                                  ) {
                                    form.setFieldValue(
                                      "hourlyLabourRate",
                                      value,
                                    );
                                  }
                                }}
                              />
                            )}
                          </Field>
                          {touched.hourlyLabourRate &&
                            errors.hourlyLabourRate && (
                              <div className="invalid-feedback d-block">
                                {errors.hourlyLabourRate}
                              </div>
                            )}
                        </div>

                        <div className="col-12 col-md-5">
                          <FormLabel
                            htmlFor="tax"
                            label={localization("tax_percentage")}
                            required
                          />
                          <Field name="tax">
                            {({ field, form }) => (
                              <input
                                {...field}
                                type="text"
                                inputMode="decimal"
                                pattern="^\d*\.?\d{0,2}$"
                                className={`form-control ${
                                  form.touched.tax && form.errors.tax
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder={localization("tax_placeholder")}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key))
                                    e.preventDefault();
                                }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    value === "" ||
                                    /^\d*\.?\d{0,2}$/.test(value)
                                  ) {
                                    form.setFieldValue("tax", value);
                                  }
                                }}
                              />
                            )}
                          </Field>
                          {touched.tax && errors.tax && (
                            <div className="invalid-feedback d-block">
                              {errors.tax}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="selectedServices"
                        label={localization("select_services")}
                        required
                      />
                      <Field name="selectedServices">
                        {({ field, form }) => (
                          <div className="multidropdown-container">
                            <Select
                              isMulti
                              name="selectedServices"
                              options={services.map((service) => {
                                const code = (
                                  service.currency ||
                                  service.currencyCode ||
                                  "USD"
                                ).toUpperCase();
                                const symbol = getCurrencySymbol(code);
                                const amount = Number(
                                  service.price ?? 0,
                                ).toFixed(2);
                                return {
                                  value: service.serviceId,
                                  label: `${service.opCode ? service.opCode + " - " : ""}${service.serviceName} (${symbol}${amount})`,
                                };
                              })}
                              classNamePrefix="select"
                              closeMenuOnSelect={false}
                              placeholder={localization("select_services")}
                              onChange={(selectedOptions) =>
                                form.setFieldValue(
                                  "selectedServices",
                                  selectedOptions.map((opt) => opt.value),
                                )
                              }
                              value={services
                                .filter((s) =>
                                  field.value.includes(s.serviceId),
                                )
                                .map((s) => {
                                  const code = (
                                    s.currency ||
                                    s.currencyCode ||
                                    "USD"
                                  ).toUpperCase();
                                  const symbol = getCurrencySymbol(code);
                                  const amount = Number(s.price ?? 0).toFixed(
                                    2,
                                  );
                                  return {
                                    value: s.serviceId,
                                    label: `${s.opCode ? s.opCode + " - " : ""}${s.serviceName} (${symbol}${amount})`,
                                  };
                                })}
                            />
                          </div>
                        )}
                      </Field>
                      {touched.selectedServices && errors.selectedServices && (
                        <div className="invalid-feedback d-block">
                          {errors.selectedServices}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormLabel
                        htmlFor="logoPath"
                        label={localization("upload_logo")}
                        required
                      />
                      <div className="input-group d-flex gap-2 edit-upload-field">
                        <input
                          type="text"
                          className={`form-control ${
                            touched.logoPath && errors.logoPath
                              ? "is-invalid"
                              : ""
                          }`}
                          value={
                            file
                              ? file.name
                              : isEditMode &&
                                  values.logoPath &&
                                  typeof values.logoPath === "string"
                                ? values.logoPath.split("/").pop()
                                : "No file chosen"
                          }
                          readOnly
                        />
                        <input
                          id="logoPath"
                          name="logoPath"
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={(e) => {
                            const selected =
                              e.currentTarget.files && e.currentTarget.files[0];
                            setFieldValue(
                              "logoPath",
                              selected || values.logoPath || "",
                            );
                            setFile(selected || null);
                            try {
                              if (
                                logoPreview &&
                                typeof logoPreview === "string" &&
                                logoPreview.startsWith("blob:")
                              ) {
                                URL.revokeObjectURL(logoPreview);
                              }
                            } catch (err) {}
                            if (selected) {
                              setLogoPreview(URL.createObjectURL(selected));
                              setUploadError("");
                            } else {
                              setLogoPreview(values.logoPath || null);
                            }
                          }}
                        />
                        <label
                          className={`btn btn-blue btn-outline-secondary ${
                            touched.logoPath && errors.logoPath
                              ? "border-danger"
                              : ""
                          }`}
                          htmlFor="logoPath"
                        >
                          <i className="fas fa-upload me-1"></i>{" "}
                          {localization("browse")}
                        </label>
                      </div>
                      {touched.logoPath && errors.logoPath && (
                        <div className="invalid-feedback d-block">
                          {errors.logoPath}
                        </div>
                      )}
                      {uploadError && (
                        <div className="text-danger small mt-2">
                          <i className="fas fa-exclamation-circle me-1"></i>{" "}
                          {uploadError}
                        </div>
                      )}
                      {logoPreview && (
                        <div
                          className="mt-2 border rounded p-2 text-center"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <p className="small text-muted mb-1">Preview:</p>
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "120px",
                              objectFit: "contain",
                            }}
                            className="img-thumbnail"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {status && status.error && (
                    <div className="alert alert-danger mt-3">
                      {status.error}
                    </div>
                  )}
                  <div className="text-center mt-4 d-flex justify-content-center">
                    <button
                      type="submit"
                      className="btn btn-success me-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {localization(isEditMode ? "update" : "save")}
                        </>
                      ) : (
                        localization(isEditMode ? "update" : "save")
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-cancel"
                      onClick={() => navigate("/services/dealership/list")}
                      disabled={isSubmitting}
                    >
                      {localization("cancel")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDealerShip;
